import { Suspense, useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { BrowserRouter as Router } from "react-router-dom";

import { isEqual } from "lodash";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

import { createTheme } from "@smartrent/tokens";
import { PlatformProvider, ThemeProvider } from "@smartrent/ui";
import { colorBrightness } from "@smartrent/utils";

import AppShellBase from "@/components/AppShellBase";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import Footer from "@/components/common/Footer";
import FullScreenLoader from "@/components/common/FullScreenLoader";
import { StateContext, initialState } from "@/context/AppState";
import { AlertProvider } from "@/context/alert";
import { BottomSheetProvider } from "@/context/bottomSheet";
import { DialogProvider } from "@/context/dialog";
import { ModalProvider } from "@/context/modal";
import { useAccessTokens } from "@/hooks/access-tokens";
import { VerificationProvider } from "@/hooks/verification";
import { ToastProvider } from "@/utils/toast";

import "./assets/styles/google_recaptcha.css";
import Routes from "./routes";

export default function App({ config }: any) {
  const [accessTokens, setAccessTokens] = useAccessTokens();

  const [state, setState] = useState({
    ...initialState,
    config,
    theme: createTheme({
      name: "tours",
      customColorAliases: (_colors, _mode) => ({
        primaryHover: colorBrightness(config?.color, "dark", 0.2),
        primary: config?.color as string,
      }),
    }),
    accessTokens,
    setAccessTokens,
  });

  useEffect(() => {
    if (!isEqual(state.accessTokens, accessTokens)) {
      setState((state) => {
        return { ...state, accessTokens };
      });
    }
  }, [accessTokens, state.accessTokens]);

  const { colors: themeColors, shadows: themeShadows } = useMemo(() => {
    const theme = state.theme("light", "web");

    return theme;
  }, [state]);

  const googleRecaptchaSiteKey =
    process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY ?? "";

  return (
    <>
      {/* @ts-ignore: Ignore BrowserRouter type error (this is because we need to upgrade react-router-dom, but can't because smartkit doesn't support it yet)  */}
      <Router>
        <ThemeProvider theme={state.theme} initialMode="light">
          <PlatformProvider>
            <ErrorBoundary>
              <View style={styles.root}>
                <StateContext.Provider value={[state, setState]}>
                  <ToastProvider>
                    <ModalProvider>
                      <DialogProvider>
                        <AlertProvider>
                          <VerificationProvider>
                            <GoogleReCaptchaProvider
                              reCaptchaKey={googleRecaptchaSiteKey}
                            >
                              <BottomSheetProvider>
                                <Suspense
                                  fallback={
                                    <AppShellBase
                                      header={{
                                        left: null,
                                      }}
                                      route={{
                                        path: "/",
                                      }}
                                      colors={themeColors}
                                      shadows={themeShadows}
                                      propertyName={
                                        (config && config.propertyName) || ""
                                      }
                                      phoneNumber={
                                        config && config.phoneNumber
                                          ? config.phoneNumber
                                          : null
                                      }
                                      showNewTourButton
                                    >
                                      <FullScreenLoader />
                                      <div />
                                    </AppShellBase>
                                  }
                                >
                                  <Routes />
                                </Suspense>
                              </BottomSheetProvider>
                            </GoogleReCaptchaProvider>
                          </VerificationProvider>
                        </AlertProvider>
                      </DialogProvider>
                    </ModalProvider>
                  </ToastProvider>
                </StateContext.Provider>
                <Footer />
              </View>
            </ErrorBoundary>
          </PlatformProvider>
        </ThemeProvider>
      </Router>
    </>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },
});
